import {Box, Container, Stack} from '@mui/material';
import React, {Fragment, useEffect, useState} from 'react';

import {
	makeDeleteRefundUploadFile,
	makePostRefundUploadFile,
} from 'main/factories/usecases/private/refund/request';
import {CTAButtonFixed, MainFooter} from 'presentation/components';
import {TermsNamesEnum} from 'presentation/hooks';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {RequestInfo, UploadRefundFiles} from 'presentation/pages/private/refund/components';
import {PaymentReceiptAlert} from 'presentation/pages/private/refund/components/PaymentReceiptAlert';
import {InvoiceTypeEnum} from 'presentation/pages/private/refund/constants/invoiceTypeEnum';
import {useRefundRequest} from 'presentation/pages/private/refund/hooks';
import {translations} from 'presentation/translations-new';
import {formatPriceRefund, formatText} from 'presentation/utils';

import {RefundTaggingEnum} from 'main/analytics/gaTagsEnum';
import {UploadModals} from 'presentation/pages/private/refund/components/UploadModals';
import {useUploadRefundFile} from 'presentation/pages/private/refund/components/UploadRefundFiles/hooks/useUploadRefundFile';
import {useUploadedRefundFilesHistoryStore} from 'presentation/pages/private/refund/components/UploadRefundFiles/store/useUploadedRefundFilesHistoryStore';
import {useUploadedRefundFilesStore} from 'presentation/pages/private/refund/components/UploadRefundFiles/store/useUploadedRefundFilesStore';
import {StyledFooterContainer} from './styles';
import {SendDocumentsProps} from './types';

const {proceedForConference} = translations['pt-br'].privateFlow.refund.sendDocuments;
const {error} = translations['pt-br'].others.uploadFiles;

export function SendDocuments({handleNextStep}: SendDocumentsProps) {
	const resetUploadedRefundFiles = useUploadedRefundFilesStore(state => state.resetUploadedFiles);
	const resetUploadedFilesHistory = useUploadedRefundFilesHistoryStore(
		state => state.resetUploadedFiles,
	);
	const setUploadedRefundFiles = useUploadedRefundFilesStore(state => state.setUploadedFiles);
	const setUploadedFilesHistory = useUploadedRefundFilesHistoryStore(
		state => state.setUploadedFiles,
	);
	const {requests, selectedRequestIndex, formAtoms, handleUpdateFormAtoms} = useRefundRequest();
	const {isMobile} = useMediaQueries();

	const {
		uploadedFiles,
		deleteFile,
		handleUpload,
		IS_DELETING,
		IS_UPLOADING,
		blockCloseModal,
		IS_DELETING_ERROR,
		resetDeleteError,
	} = useUploadRefundFile({
		protocolCode: requests.protocolCode,
		requestCode: requests.requestData
			? requests.requestData[selectedRequestIndex].requestCode
			: 0,
		postFactory: makePostRefundUploadFile,
		deleteFactory: makeDeleteRefundUploadFile,
		clearErrorMessage: () => setErrorMessageState(null),
	});

	const someFileIsUploaded = uploadedFiles?.some(file => file.uploaded === true);
	const currentRequest = requests.requestData[selectedRequestIndex];
	const isCNPJ = currentRequest.professionalData.professionalType === 'J';
	const [errorMessageState, setErrorMessageState] = useState<string | null>(null);
	const [sendingAttachState, setSendingAttachState] = useState<boolean>(false);

	const handleProceed = () => {
		if (IS_UPLOADING) {
			setSendingAttachState(true);
			return;
		}

		if (!someFileIsUploaded) {
			setErrorMessageState(error);
			return;
		}
		setErrorMessageState(null);
		resetUploadedRefundFiles();
		resetUploadedFilesHistory();
		handleNextStep();
	};

	useEffect(() => {
		if (uploadedFiles.length >= 1) {
			handleUpdateFormAtoms.uploads(uploadedFiles);
		}
	}, [uploadedFiles]);

	useEffect(() => {
		if (formAtoms.uploads && formAtoms.uploads?.length >= 1) {
			setUploadedRefundFiles(() => formAtoms.uploads || []);
			setUploadedFilesHistory(() => formAtoms.uploads || []);
		}
	}, []);

	return (
		<Fragment>
			<PaymentReceiptAlert />

			<Container>
				<Stack mt={4} spacing={2}>
					{requests.requestData ? (
						<Box borderRadius={1}>
							<Stack spacing={2}>
								<Box>
									<RequestInfo
										city={formatText(
											currentRequest.financialData.invoiceIssueCity,
										)}
										state={currentRequest.financialData.invoiceIssueState}
										patientName={
											formatText(currentRequest.beneficiaryName) || ''
										}
										doctorName={formatText(
											currentRequest.professionalData.professionalName,
										)}
										providerName={
											isCNPJ
												? formatText(
														currentRequest.professionalData
															?.providerName,
													)
												: '-'
										}
										specialtyName={formatText(
											currentRequest.treatmentData.specialty.description,
										)}
										date={currentRequest.treatmentData.date.replaceAll(
											'-',
											'/',
										)}
										numFiscalDocument={String(
											currentRequest.financialData.invoiceNumber,
										)}
										numFiscalDigit={currentRequest.financialData.invoiceDigit}
										value={formatPriceRefund(
											currentRequest.financialData.invoiceAmount,
										)}
										hasNfe={
											currentRequest.financialData.invoiceType ===
											InvoiceTypeEnum.EMIT
										}
									/>
								</Box>
							</Stack>
						</Box>
					) : null}

					<UploadRefundFiles
						errorDataTagId={RefundTaggingEnum.ERRO_ADICIONE_UM_ARQUIVO_REEMBOLSO}
						dataTagId={RefundTaggingEnum.CLICK_ADICIONAR_ARQUIVOS_REEMBOLSO}
						removeFile={deleteFile}
						shouldShowTermsModal={{
							show: true,
							termName: TermsNamesEnum.UPLOAD_FILES,
						}}
						onChangeFile={handleUpload}
						values={uploadedFiles}
						isUploading={IS_UPLOADING}
						externalError={errorMessageState}
						setExternaErrorMessage={setErrorMessageState}
					/>

					<CTAButtonFixed.Root
						direction={!isMobile ? 'row' : 'column'}
						justifyContent="flex-end">
						<CTAButtonFixed.Action
							data-tag-id={RefundTaggingEnum.CLICK_PROSSEGUIR_PARA_REVISAO_REEMBOLSO}
							size="large"
							onClick={handleProceed}>
							{proceedForConference}
						</CTAButtonFixed.Action>
					</CTAButtonFixed.Root>
				</Stack>
			</Container>

			<UploadModals
				openErrorModal={IS_DELETING_ERROR}
				closeErrorModa={resetDeleteError}
				openSendingAttachModal={sendingAttachState}
				closeSendingAttachModal={() => setSendingAttachState(false)}
				openPendingDialog={IS_DELETING}
				closePendingDialog={blockCloseModal}
			/>

			<StyledFooterContainer>
				<MainFooter />
			</StyledFooterContainer>
		</Fragment>
	);
}
